import type { PathnameWithSearchOrHash, ResolvedPathname } from '$app/types';
import { bareRecordingId } from '$lib/api/recordings';
import { resolveHref } from '$lib/utils/routing/resolveHref';
import type { RecentCard } from './RecentPracticeCard.svelte';

export const TAKE_URL_PARAM = 'take';
// Shows a take on the library grid (scrolled to, highlighted, marked current) without opening it.
export const FOCUS_URL_PARAM = 'focus';

const TAKE_ID = /^(offline:)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function takeParam(url: URL): string | null {
  return url.searchParams.get(TAKE_URL_PARAM);
}

export function urlWithoutTakeParam(url: URL): ResolvedPathname {
  const stripped = new URL(url);
  stripped.searchParams.delete(TAKE_URL_PARAM);
  return here(stripped);
}

export function focusParam(url: URL): string | null {
  return url.searchParams.get(FOCUS_URL_PARAM);
}

export function libraryFocusHref(takeId: string): ResolvedPathname {
  return resolveHref(`/library?${FOCUS_URL_PARAM}=${encodeURIComponent(bareRecordingId(takeId))}`);
}

export function urlWithoutFocusParam(url: URL): ResolvedPathname {
  const stripped = new URL(url);
  stripped.searchParams.delete(FOCUS_URL_PARAM);
  return here(stripped);
}

export function urlWithTakeParam(url: URL, sessionId: string): ResolvedPathname {
  const linked = new URL(url);
  linked.searchParams.set(TAKE_URL_PARAM, bareRecordingId(sessionId));
  return here(linked);
}

function here(url: URL): ResolvedPathname {
  return resolveHref(`${url.pathname}${url.search}${url.hash}` as PathnameWithSearchOrHash);
}

// Untrusted param, matched on the BARE id: a local take is keyed `offline:<guid>`.
export function findLinkedTake(linked: string, cards: RecentCard[]): RecentCard | null {
  if (!TAKE_ID.test(linked)) return null;
  const wanted = bareRecordingId(linked);
  return cards.find((c) => bareRecordingId(c.sessionId) === wanted) ?? null;
}

export function takeAnchorId(sessionId: string): string {
  return `take-${sessionId}`;
}
